/**
 * Frido Product Group runtime.
 *
 * One placement renders the members of a Spectrum Product Group
 * (spectrum.product_group_refs_live) as text pills or image tiles. Picking a
 * member navigates to that sibling product. Groups-only by contract — variant
 * / option axes render via a separate snippet.
 *
 * Display mode: `group_display` prop wins ('tiles' | 'pills'); 'auto' derives
 * from the group's authored kind and only shows tiles when every member has a
 * resolved image (otherwise text pills). Authored per-value badges come from
 * `group.badges_map` — a value-keyed map indexed by member label.
 */
;(() => {
  if (window.__sai_fpgfrd01_initialized__) return
  window.__sai_fpgfrd01_initialized__ = true

  // Compare-variants dialog. The trigger is emitted on the group header (both
  // SSR and _render(), in lockstep); the <dialog> is a sibling of <sai-fpgfrd01>
  // so it survives the element's innerHTML re-render, and is resolved by the
  // trigger's aria-controls id. Listeners are delegated on document, so a trigger
  // that appears after first paint (re-render / SDK re-bind) still works.
  ;(function setupCompareDialog() {
    const OPEN = 'sai-fcvfrd01__dialog--open'
    const dialogFor = (trigger) => {
      const id = trigger && trigger.getAttribute && trigger.getAttribute('aria-controls')
      return (id && document.getElementById(id)) || document.querySelector('[data-sai-fcv-dialog]')
    }
    const openDialog = (trigger) => {
      const d = dialogFor(trigger)
      if (!d || d.open) return
      d.showModal()
      document.body.style.overflow = 'hidden'
      void d.offsetHeight
      d.classList.add(OPEN)
    }
    const closeDialog = (d) => {
      if (!d || !d.open) return
      d.classList.remove(OPEN)
      const wrap = d.querySelector('.sai-fcvfrd01__wrap')
      const htm = d.querySelector('[data-sai-fcv-htm]')
      if (htm) htm.dataset.open = 'false'
      let done = false
      const finalize = () => { if (done) return; done = true; if (d.open) d.close(); document.body.style.overflow = '' }
      if (wrap) wrap.addEventListener('transitionend', function h(e) {
        if (e.propertyName !== 'transform' && e.propertyName !== 'opacity') return
        wrap.removeEventListener('transitionend', h); finalize()
      })
      setTimeout(finalize, 550)
    }
    const toggleHtm = (d, open) => {
      const htm = d && d.querySelector('[data-sai-fcv-htm]')
      if (!htm) return
      htm.dataset.open = open ? 'true' : 'false'
      htm.setAttribute('aria-hidden', open ? 'false' : 'true')
    }
    document.addEventListener('click', (e) => {
      const t = e.target
      if (!(t instanceof Element)) return
      const openBtn = t.closest('[data-sai-fcv-open]')
      if (openBtn) { openDialog(openBtn); return }
      const closeBtn = t.closest('[data-sai-fcv-close]')
      if (closeBtn) { closeDialog(closeBtn.closest('[data-sai-fcv-dialog]')); return }
      const openHtm = t.closest('[data-sai-fcv-open-htm]')
      if (openHtm) { toggleHtm(openHtm.closest('[data-sai-fcv-dialog]'), true); return }
      const closeHtm = t.closest('[data-sai-fcv-close-htm]')
      if (closeHtm) { toggleHtm(closeHtm.closest('[data-sai-fcv-dialog]'), false); return }
    })
    document.addEventListener('cancel', (e) => {
      const d = e.target
      if (!(d instanceof HTMLDialogElement) || !d.matches('[data-sai-fcv-dialog]')) return
      e.preventDefault()
      const htm = d.querySelector('[data-sai-fcv-htm]')
      if (htm && htm.dataset.open === 'true') { toggleHtm(d, false); return }
      closeDialog(d)
    })
    document.addEventListener('click', (e) => {
      const d = e.target
      if (!(d instanceof HTMLDialogElement) || !d.matches('[data-sai-fcv-dialog]')) return
      const wrap = d.querySelector('.sai-fcvfrd01__wrap')
      if (!wrap) return
      const r = wrap.getBoundingClientRect()
      const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom
      if (!inside) closeDialog(d)
    })
  })()

  const SNIPPET_ID = 'fpgfrd01'
  const TAG = 'sai-fpgfrd01'
  const FEATURE_SLUG = 'product_groups'

  function esc(s) {
    return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
  }
  function readPool(node) {
    for (let i = 0; i < node.children.length; i++) {
      const c = node.children[i]
      if (c.tagName === 'SCRIPT' && c.hasAttribute('data-spectrum-snippet-pool')) {
        try { return JSON.parse(c.textContent) } catch (e) { return {} }
      }
    }
    return {}
  }

  // Badge pill ({ label, bg, fg, icon }). Icon is a URL (<img>) or a short
  // glyph. Colours are authored data written into inline style, esc()-guarded
  // against attribute breakout. Missing label -> skip.
  function badgePill(b) {
    if (!b || !b.label) return ''
    const bg = b.bg || '#000000'
    const fg = b.fg || '#ffffff'
    const icon = b.icon
      ? (/^(https?:)?\/\//.test(b.icon)
          ? `<img src="${esc(b.icon)}" alt="" loading="lazy" decoding="async">`
          : `<span aria-hidden="true">${esc(b.icon)}</span>`)
      : ''
    return `<span class="sai-fpgfrd01__badge" style="background:${esc(bg)};color:${esc(fg)}">${icon}${esc(b.label)}</span>`
  }
  function badgesHtml(list) {
    return Array.isArray(list) && list.length
      ? `<span class="sai-fpgfrd01__badges">${list.map(badgePill).join('')}</span>`
      : ''
  }

  const El = class extends HTMLElement {
    connectedCallback() {
      if (this._init) return
      this._init = true
      this._track = () => {}
    }
    setAnalytics(t) { this._track = typeof t === 'function' ? t : () => {} }

    applyVariant(_content, pool) {
      this._pool = pool && typeof pool === 'object' ? pool : {}
      const p = this._pool.product
      if (!p || !p.group) return
      this._render()
      this._bind()
    }

    _props() { return (this._pool && this._pool.props) || {} }

    _render() {
      const p = this._pool.product
      const props = this._props()
      let html = ''

      if (p.group && Array.isArray(p.group.products) && p.group.products.length > 1) {
        const current = p.group.products.find((g) => g.current)
        // heading: config.heading wins; then the placement prop; then the name.
        let heading = p.group.heading || props.group_label || (p.group.name + ' :')
        // Default: heading only (the highlighted card signals the current
        // choice). group_show_selected opts into the "KEY : value" form.
        const showSel = props.group_show_selected === true && current
        if (showSel && !/:\s*$/.test(heading)) heading += ' :'
        // Compare-variants trigger on the header row — MUST stay in lockstep with
        // the SSR label markup in _sai-snippet-fpgfrd01.liquid. `compare` is null
        // when the product has no comparison image.
        const cv = (this._pool.product && this._pool.product.compare) || null
        const compareBtn = cv && cv.on
          ? `<button type="button" class="sai-fpgfrd01__compare" data-sai-fcv-open aria-controls="${esc(cv.id)}">${esc(cv.label || 'Compare Variants')}</button>`
          : ''
        const label = `<div class="sai-fpgfrd01__label"><span class="sai-fpgfrd01__label-key">${esc(heading)}</span>${showSel ? `<span class="sai-fpgfrd01__label-val">${esc(current.title)}</span>` : ''}${compareBtn}</div>`
        // Display mode: group_display prop wins ('tiles'|'pills'), else 'auto'
        // derives from the group's authored kind. Tiles require every member to
        // carry a resolved image, else fall back to pills.
        const gd = props.group_display || 'auto'
        const asTiles = gd === 'pills'
          ? false
          : (gd === 'tiles' || p.group.display === 'tiles') && p.group.products.every((g) => g.image)
        // Authored per-value badge map (value-keyed): {"<label>":[{label,bg,fg,icon}]}.
        // Per-member `badges` (future first-class value.badge) wins if present.
        let badgeMap = {}
        try { if (p.group.badges_map) badgeMap = JSON.parse(p.group.badges_map) || {} } catch (e) {}
        let body = `<div class="sai-fpgfrd01__cards" data-fit="${asTiles ? 'tiles' : 'grid'}">`
        for (const g of p.group.products) {
          // tile+image ONLY in tiles mode; pills mode renders text even when the
          // member carries an asset.
          const inner = asTiles && g.image ? `<img src="${esc(g.image)}" alt="${esc(g.title)}" loading="lazy" decoding="async">` : esc(g.title)
          const tileCls = asTiles && g.image ? ' sai-fpgfrd01__card--tile' : ''
          const authored = (g.badges && g.badges.length) ? g.badges : (badgeMap[g.title] || [])
          const badgeHtml = props.show_badges !== false ? badgesHtml(authored) : ''
          body += `<span class="sai-fpgfrd01__cell">${badgeHtml}<button type="button" class="sai-fpgfrd01__card${tileCls}${g.current ? ' is-sel' : ''}" data-action="goto" data-url="${esc(g.current ? '' : g.url)}" aria-label="${esc(g.title)}">${inner}</button></span>`
        }
        body += '</div>'
        html += `<div class="sai-fpgfrd01__axis" data-axis="__group__">${label}${body}</div>`
      }

      // An empty render must leave the host with no children — the base CSS collapses
      // the host via :empty, and any wrapper div (even an empty one) would defeat it.
      this.innerHTML = html ? `<div class="sai-fpgfrd01__inner">${html}</div>` : ''
    }

    _bind() {
      if (this._clickBound) return
      this._clickBound = true
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-action="goto"]')
        if (!t) return
        if (t.dataset.url) {
          this._track(`${FEATURE_SLUG}:group_navigate`, { url: t.dataset.url })
          window.location.href = t.dataset.url
        }
      })
    }
  }

  if (!customElements.get(TAG)) customElements.define(TAG, El)

  function bindAll() {
    const api = window.__spectrumAi && window.__spectrumAi.snippet
    for (const node of document.querySelectorAll(`[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`)) {
      const el = node.querySelector(TAG)
      if (!el) continue
      // First paint straight from the server-rendered pool — the pool's props
      // are the instance's default config, so rendering never waits on the SDK.
      if (!el._painted) {
        const initPool = readPool(node)
        if (initPool && initPool.product) { el._painted = true; el.applyVariant({}, initPool) }
      }
      if (api && typeof api.bind === 'function' && !el._wrapBound) {
        el._wrapBound = true
        const handles = api.bind(node, (ctx) => {
          const e = node.querySelector(TAG)
          if (e) e.applyVariant(ctx.variant ? ctx.variant.content : {}, readPool(node))
        })
        if (handles) el.setAnalytics(handles.track)
      }
    }
    for (const el of document.querySelectorAll(TAG)) {
      if (el._applied || el._wrapBound || el.closest('[data-spectrum-instance-id]')) continue
      const parent = el.closest('[data-spectrum-lq-snippet]') || el.parentElement
      if (!parent) continue
      const pool = readPool(parent)
      if (pool && pool.product) { el._applied = true; el.applyVariant({}, pool) }
    }
  }

  function schedule() {
    bindAll()
    // the SDK can load after us — retry until it binds (or give up quietly)
    let tries = 0
    const t = setInterval(() => {
      bindAll()
      const api = window.__spectrumAi && window.__spectrumAi.snippet
      if ((api && typeof api.bind === 'function') || ++tries > 20) clearInterval(t)
    }, 500)
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true })
  } else {
    schedule()
  }

  // The storefront SDK can re-mount slot wrappers after load (conflict
  // resolution / vis flips) — a replaced wrapper holds a fresh unpainted
  // element, so rebind on DOM changes, debounced.
  const __saiRelevant = (records) => {
    for (const r of records) {
      for (const n of r.addedNodes) {
        if (n.nodeType !== 1) continue
        if ((n.matches && n.matches(`${TAG}, [data-spectrum-snippet-id="${SNIPPET_ID}"], [data-spectrum-lq-snippet]`)) ||
            (n.querySelector && n.querySelector(`${TAG}, [data-spectrum-snippet-id="${SNIPPET_ID}"]`))) return true
      }
    }
    return false
  }
  const __saiMo = new MutationObserver((records) => {
    if (__saiMo._t || !__saiRelevant(records)) return
    __saiMo._t = setTimeout(() => { __saiMo._t = null; bindAll() }, 150)
  })
  __saiMo.observe(document.documentElement, { childList: true, subtree: true })
  setTimeout(function () { try { __saiMo.disconnect() } catch (e) {} }, 30000)
})()
